import { BasketItem } from './basket';
import { WindowBasketItem } from '../hooks/useWindowBasket';

// Create a type that matches the BasketItem interface
export type WindowOrderItem = BasketItem;

// Convert WindowBasketItem to WindowOrderItem (BasketItem) for use in orders
export const convertToWindowOrderItem = (item: WindowBasketItem): BasketItem => {
  // Always use the exact imagePath from the item if available
  // If missing, set imageUrl to an empty string (UI should warn before sending email)
  const imageUrl = item.imagePath || '';
  const styleNameOverride = String((item as any).styleName || (item as any).styleDisplayName || '').trim();
  const trickleVentsPosition = (item as any).trickleVentsPosition || null;
  const handleColor = String((item as any).handleColor || '').trim();
  const handleStyle = String((item as any).handleStyle || '').trim();
  const hingePlacement = String((item as any).hingePlacement || '').trim();
  const openDirection = String((item as any).openDirection || '').trim();
  const windowMaterial = String((item as any).windowMaterial || '').trim();
  const hingeType = String((item as any).hingeType || '').trim();
  const astragalBarSide = String((item as any).astragalBarSide || '').trim();
  const imagePresentationOutside = String((item as any).imagePresentationOutside || '').trim();
  const imagePresentationInside = String((item as any).imagePresentationInside || '').trim();
  const imageDesignOutside = String((item as any).imageDesignOutside || '').trim();
  const imageDesignInside = String((item as any).imageDesignInside || '').trim();
  const imageBaySchematic = String((item as any).imageBaySchematic || '').trim();
  const bayTangentWidthMm = String((item as any).bayTangentWidthMm ?? '').trim();
  const bayLegacyWidthMm = String((item as any).bayLegacyWidthMm ?? '').trim();
  const bayProjectionDepthMm = String((item as any).bayProjectionDepthMm ?? '').trim();
  const bayFacetWidthsMm = String((item as any).bayFacetWidthsMm ?? '').trim();
  const bayJointAnglesDeg = String((item as any).bayJointAnglesDeg ?? '').trim();
  const cillPlacementLabel = String((item as any).cillPlacementLabel || '').trim();
  const windowGeneratorPayload = (item as any).windowGeneratorPayload ?? null;
  const designerSummaryRows = Array.isArray((item as any).designerSummaryRows) ? (item as any).designerSummaryRows : undefined;

  return {
    id: item.id,
    section: 'Window', // Explicitly mark this as a window item
    thickness: item.glassType || 'Clear Glass', // Glass type
    makeup: '', // Not applicable for windows
    type: `Window - ${item.frameColor || 'White'}`, // Include frame color in type
    width: item.width || 0, // Ensure width is a number
    height: item.height || 0, // Ensure height is a number
    shape: 'Rectangle', // Default shape for windows
    spacerColour: '', // Not applicable for windows
    softCoat: false, // Not applicable for windows
    // Important: Also add properties directly to the item, not just in extras
    // This helps the email template access them more reliably
    windowType: item.windowType,
    windowStyle: item.windowStyle,
    imagePath: item.imagePath,
    extras: {
      // Transfer all window-specific properties to extras for email template use
      windowType: item.windowType,
      windowStyleId: item.windowStyle,
      styleName: styleNameOverride || (item.windowType ?
        `${item.windowType} Style ${item.windowStyle}` :
        `Style ${item.windowStyle}`), // Prefer real style name if available
      frameColor: item.frameColor,
      windowColour: item.frameColor,
      windowMaterial,
      material: windowMaterial,
      glassType: item.glassType,
      clearGlassType: item.clearGlassType,
      obscuredGlassType: item.obscuredGlassType,
      profileDepth: item.profileDepth,
      profileType: item.profileType,
      beadingType: item.beadingType,
      handleColor,
      handleStyle,
      hingeType,
      astragalBarSide,
      hingePlacement,
      openDirection,
      cillRequired: item.cillRequired,
      cillIncluded: item.cillIncluded,
      cillHeight: item.cillHeight,
      cillType: item.cillType,
      trickleVents: item.trickleVents,
      trickleVentsPosition,
      notes: item.notes,
      reference: item.reference,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      imageUrl: imageUrl, // Always use the exact primary image path
      imagePresentationOutside,
      imagePresentationInside,
      imageDesignOutside,
      imageDesignInside,
      imageBaySchematic,
      bayTangentWidthMm,
      bayLegacyWidthMm,
      bayProjectionDepthMm,
      bayFacetWidthsMm,
      bayJointAnglesDeg,
      cillPlacementLabel,
      windowGeneratorPayload,
      ...(designerSummaryRows ? { designerSummaryRows } : {}),
    },
    deduction: '',
    pricePerUnit: item.price,
    quantity: item.quantity,
    area: ((item.width || 0) * (item.height || 0)) / 1000000, // Convert to square meters
    total: item.price * item.quantity,
    effectiveType: 'window', // Explicitly mark as window type
    inclVAT: false,
    glassReference: item.reference || `Window-${Date.now().toString().slice(-4)}`, // Use window reference as glass reference
    notes: item.notes,
  };
};

// Convert array of WindowBasketItems to BasketItems
export const convertWindowItemsForOrder = (items: WindowBasketItem[]): BasketItem[] => {
  return items.map(item => convertToWindowOrderItem(item));
};

export type { WindowBasketItem } from '../hooks/useWindowBasket'; 